import React, { useEffect, useState } from 'react';
import PageTitle from 'shared/components/PageTitle/PageTitle';
import { GetMarketingAllowedPreferences, GetUserMarketingPreferences, UpdateUserMarketingPreferences } from 'services/MyRacService';
import { loggerFunc } from 'utils/logger';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROOT } from 'constants/routeConstants';
import Toast, { ToastType, TToast } from 'components/Toast/Toast';
import Spinner from 'components/Spinner';
import trackEvent from 'services/AnalyticsService';
import styles from './MarketingPermission.module.scss';

const logger = loggerFunc('MarketingPermission');

type TMarketingPreferences = {
  isInInclusionList: boolean
}

type TUserMarketingPreferences = {
  id: string,
  email: string,
  generalPreferences: {
    emailMarketing: boolean,
    referrerSource: string | null
  },
  productPreferences: object
}

const MarketingPermission: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [toast, setToast] = useState<TToast | null>((location?.state as { toast: TToast})?.toast || null);
  const [saveStatus, setSaveStatus] = useState<'pending' | 'success' | 'error' | undefined>()
  const [loading, setLoading] = useState(true);
  const [marketingPreferences, setMarketingPreferences] = useState<TUserMarketingPreferences>();

  const createToast: TToast = {
    msg: 'Preferences updated',
    type: ToastType.SUCCESS,
  };

  const routeToHomePage = () => {
    trackEvent('MyRAC_MarketingPermission_Page_Saved');
    navigate(ROOT, { state: { toast: createToast } })
  };

  const handleSubmit = async () => {
    setSaveStatus('pending');

    try {
      const response = await UpdateUserMarketingPreferences();
      if (response.responseCode === 'success') routeToHomePage();
      else setSaveStatus('error');
    } catch (err) {
      logger.debug('Failed to save marketing preferences:', err);
      setSaveStatus('error');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [data, userData] = await Promise.all([
          GetMarketingAllowedPreferences(),
          GetUserMarketingPreferences(),
        ]) as [TMarketingPreferences, TUserMarketingPreferences];

        if (!data.isInInclusionList) navigate(ROOT);
        if (userData) setMarketingPreferences(userData);

        trackEvent('MyRAC_MarketingPermission_Page_View');
      } catch (err) {
        logger.debug(err);
        setSaveStatus('error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Spinner bootSpinner />;

  return (
    <div className={styles.container}>
      {toast && <Toast msg={toast.msg} type={toast.type} closeHandler={() => setToast(null)} />}
      <PageTitle text="Thanks for letting us know" />

      {marketingPreferences?.generalPreferences?.emailMarketing && (
        <div className={styles.marketingPreferences}>Email marketing: {marketingPreferences?.generalPreferences?.emailMarketing ? 'enabled' : 'disabled'}</div>
      )}

      <p className={styles.paragraph}>While you’re here, can we stay in touch about your exclusive member benefits?</p>
      <ul className={styles.paragraph}>
        <li>Discounts on car servicing and repairs at home with RAC Mobile Mechanics</li>
        <li>Money off fuel, tyres and more</li>
        <li>Chances to enter our free member prize draws</li>
      </ul>
      <p className={styles.paragraph}>We’ll never share your data - it’s protected in line with our <a href="/legal-information/privacy-policy" target="_blank">Privacy Policy</a>.</p>
      <div className="mb-5">
        <button type="submit" disabled={saveStatus === 'pending'} className={styles.saveButton} onClick={handleSubmit}>
          {saveStatus === 'pending' ? <i className="fas fa-spinner fa-pulse" /> : 'Yes please'}
        </button>

        {saveStatus !== 'pending' && (
          <button type="button" className={styles.cancelButton} onClick={() => navigate(ROOT)}>
            Not now
          </button>
        )}
      </div>
    </div>
  );
};

export default MarketingPermission;
